'use client';

import {
  useMemo,
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Minus,
  ArrowsLeftRight,
  Equals,
  Warning,
  Check,
  ArrowRight,
} from '@phosphor-icons/react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface DiffResultViewProps {
  data: Record<string, unknown>[];
  columns: string[];
  metadata?: Record<string, unknown>;
}

/** Ref handle exposed to parent for driving the diff-aware Excel download. */
export interface DiffResultViewRef {
  getExportData(): { columns: string[]; rows: string[][] };
}

type DiffType = 'added' | 'removed' | 'changed' | 'unchanged' | string;
type Decision = 'old' | 'new';

// ── Go / JSON parsers ─────────────────────────────────────────────────────────

function parseGoMap(raw: unknown): Record<string, string> | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.fromEntries(
      Object.entries(raw as Record<string, unknown>).map(([k, v]) => [k, String(v)])
    );
  }
  if (typeof raw !== 'string') return null;
  if (raw.startsWith('{')) {
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object' && !Array.isArray(p)) {
        return Object.fromEntries(Object.entries(p).map(([k, v]) => [k, String(v)]));
      }
    } catch { /* fall through */ }
  }
  if (raw === 'map[]' || raw === '{}') return {};
  const m = raw.match(/^map\[(.+)\]$/s);
  if (!m) return null;
  const inner = m[1].trim();
  const result: Record<string, string> = {};
  const keyRe = /(\w+):/g;
  const positions: Array<{ key: string; valueStart: number }> = [];
  let km: RegExpExecArray | null;
  while ((km = keyRe.exec(inner)) !== null) {
    positions.push({ key: km[1], valueStart: km.index + km[0].length });
  }
  for (let i = 0; i < positions.length; i++) {
    const { key, valueStart } = positions[i];
    const end =
      i + 1 < positions.length
        ? positions[i + 1].valueStart - positions[i + 1].key.length - 2
        : inner.length;
    result[key] = inner.slice(valueStart, end).trim();
  }
  return Object.keys(result).length > 0 ? result : {};
}

/** 解析 changes 字段：支持字符串数组（旧格式）和对象数组（新格式）。
 *  返回 { column, oldValue, newValue } 数组。 */
interface FieldChange {
  column: string;
  oldValue?: string;
  newValue?: string;
}

function parseChanges(raw: unknown): FieldChange[] {
  if (raw === null || raw === undefined) return [];
  let arr: unknown[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'string' && raw.startsWith('[')) {
    try { arr = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(arr) || arr.length === 0) return [];
  // 新格式：[{ column, oldValue, newValue }, ...]
  if (typeof arr[0] === 'object' && arr[0] !== null && 'column' in (arr[0] as object)) {
    return (arr as Array<{ column: string; oldValue?: unknown; newValue?: unknown }>).map((c) => ({
      column: String(c.column),
      oldValue: c.oldValue !== undefined && c.oldValue !== null ? String(c.oldValue) : undefined,
      newValue: c.newValue !== undefined && c.newValue !== null ? String(c.newValue) : undefined,
    }));
  }
  // 旧格式：["col1", "col2", ...]
  return arr.map((c) => ({ column: String(c) }));
}

// ── Row styles ─────────────────────────────────────────────────────────────────

const DIFF_STYLE: Record<
  string,
  { rowBg: string; badge: string; icon: React.ElementType; iconCls: string; label: string }
> = {
  added:     { rowBg: 'bg-emerald-50/70', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Plus,          iconCls: 'text-emerald-600', label: '新增' },
  removed:   { rowBg: 'bg-red-50/70',     badge: 'bg-red-100 text-red-700 border-red-200',             icon: Minus,         iconCls: 'text-red-600',     label: '删除' },
  changed:   { rowBg: 'bg-amber-50/60',   badge: 'bg-amber-100 text-amber-800 border-amber-200',       icon: ArrowsLeftRight,iconCls: 'text-amber-600',  label: '变更' },
  unchanged: { rowBg: 'bg-white',         badge: 'bg-zinc-100 text-zinc-500 border-zinc-200',          icon: Equals,        iconCls: 'text-zinc-400',    label: '未变' },
};

const fallbackStyle = DIFF_STYLE.unchanged;

// ── Summary Banner ─────────────────────────────────────────────────────────────

function SummaryBanner({
  total, added, removed, changed, unchanged,
  onAcceptAllNew, onKeepAllOld,
}: {
  total: number; added: number; removed: number; changed: number; unchanged: number;
  onAcceptAllNew?: () => void; onKeepAllOld?: () => void;
}) {
  const badges = [
    { label: '新增', count: added,     color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { label: '删除', count: removed,   color: 'bg-red-100 text-red-700 border-red-200' },
    { label: '变更', count: changed,   color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { label: '未变', count: unchanged, color: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  ];
  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex-shrink-0 flex-wrap">
      <div className="flex items-center gap-1.5">
        <ArrowsLeftRight className="w-3.5 h-3.5 text-violet-500" weight="fill" />
        <span className="text-xs font-semibold text-zinc-700">差异对比</span>
        <span className="text-[10px] text-zinc-400 font-mono">共 {total} 行</span>
      </div>
      <div className="flex items-center gap-1.5">
        {badges.map(({ label, count, color }) => (
          <span
            key={label}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${color}`}
          >
            {label} <span className="font-bold">{count}</span>
          </span>
        ))}
      </div>
      {(onKeepAllOld || onAcceptAllNew) && (
        <div className="ml-auto flex items-center gap-1.5">
          {onKeepAllOld && (
            <button
              onClick={onKeepAllOld}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-white border border-zinc-200 text-zinc-600 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
            >
              保留所有旧值
            </button>
          )}
          {onAcceptAllNew && (
            <button
              onClick={onAcceptAllNew}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-white border border-zinc-200 text-zinc-600 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 transition-colors"
            >
              <ArrowRight className="w-3 h-3" />
              采用所有新值
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── DiffCell ──────────────────────────────────────────────────────────────────

interface DiffCellProps {
  diffType: DiffType;
  oldVal?: string;
  newVal?: string;
  isChanged: boolean;
  decision: Decision;
  onDecide: (d: Decision) => void;
}

function DiffCell({ diffType, oldVal, newVal, isChanged, decision, onDecide }: DiffCellProps) {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const tdRef = useRef<HTMLTableCellElement>(null);
  const hideTimerRef = useRef<number | null>(null);

  const computePos = useCallback(() => {
    if (!tdRef.current) return;
    const rect = tdRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setPopoverStyle({
      position: 'fixed',
      top: spaceBelow > 170 ? rect.bottom + 3 : rect.top - 173,
      left: Math.min(rect.left, window.innerWidth - 244),
      zIndex: 9999,
      width: 240,
    });
  }, []);

  // 延迟隐藏，给鼠标移动留出时间
  const handleMouseEnter = useCallback(() => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    computePos();
    setShowPopover(true);
  }, [computePos]);

  const handleMouseLeave = useCallback(() => {
    hideTimerRef.current = window.setTimeout(() => {
      setShowPopover(false);
    }, 150); // 150ms 延迟，允许鼠标移动到 popover
  }, []);

  let displayVal: string;
  let cellCls: string;

  if (diffType === 'unchanged') {
    displayVal = oldVal ?? '';
    cellCls = '';
  } else if (diffType === 'added') {
    displayVal = newVal ?? '';
    cellCls = 'bg-emerald-50 text-emerald-900';
  } else if (diffType === 'removed') {
    displayVal = oldVal ?? '';
    cellCls = 'bg-red-50 text-red-800';
  } else if (diffType === 'changed' && isChanged) {
    displayVal = decision === 'new' ? (newVal ?? '') : (oldVal ?? '');
    cellCls = decision === 'new' ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-800';
  } else {
    displayVal = oldVal ?? newVal ?? '';
    cellCls = '';
  }

  const interactive = diffType === 'changed' && isChanged;

  if (!interactive) {
    return (
      <td
        className={`px-3 py-2 text-xs align-middle border-r border-zinc-100 last:border-r-0 ${cellCls}`}
      >
        <span
          className={`block truncate max-w-[160px] ${diffType === 'removed' ? 'line-through opacity-70' : ''}`}
        >
          {displayVal || <span className="text-zinc-300">—</span>}
        </span>
      </td>
    );
  }

  return (
    <td
      ref={tdRef}
      className={`px-3 py-2 text-xs align-middle border-r border-zinc-100 cursor-pointer select-none ${cellCls}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center gap-1 max-w-[160px]">
        <span className="truncate flex-1 min-w-0">
          {displayVal || <span className="text-zinc-300">—</span>}
        </span>
        <span
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
            decision === 'new' ? 'bg-emerald-400' : 'bg-amber-400'
          }`}
        />
      </div>

      {showPopover &&
        createPortal(
          <div
            style={popoverStyle}
            className="bg-white rounded-xl border border-zinc-200 shadow-2xl overflow-hidden"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="px-3 py-1.5 bg-zinc-50 border-b border-zinc-100 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
              选择保留的值
            </div>

            {/* Keep old */}
            <button
              className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors ${
                decision === 'old' ? 'bg-red-50' : 'hover:bg-zinc-50'
              }`}
              onClick={(e) => { e.stopPropagation(); onDecide('old'); setShowPopover(false); }}
            >
              <div
                className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  decision === 'old' ? 'border-red-400 bg-red-400' : 'border-zinc-300'
                }`}
              >
                {decision === 'old' && <Check className="w-2 h-2 text-white" weight="bold" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] text-zinc-400 mb-0.5">保留旧值</div>
                <div className="text-xs text-red-700 font-medium truncate">{oldVal ?? '—'}</div>
              </div>
            </button>

            {/* Accept new */}
            <button
              className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 transition-colors border-t border-zinc-100 ${
                decision === 'new' ? 'bg-emerald-50' : 'hover:bg-zinc-50'
              }`}
              onClick={(e) => { e.stopPropagation(); onDecide('new'); setShowPopover(false); }}
            >
              <div
                className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                  decision === 'new' ? 'border-emerald-500 bg-emerald-500' : 'border-zinc-300'
                }`}
              >
                {decision === 'new' && <Check className="w-2 h-2 text-white" weight="bold" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] text-zinc-400 mb-0.5">采用新值</div>
                <div className="text-xs text-emerald-700 font-medium truncate">{newVal ?? '—'}</div>
              </div>
            </button>
          </div>,
          document.body
        )}
    </td>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export const DiffResultView = forwardRef<DiffResultViewRef, DiffResultViewProps>(
  function DiffResultView({ data, columns }, ref) {
    const [decisions, setDecisions] = useState<Record<string, Decision>>({});

    const handleDecide = useCallback((rowIdx: number, col: string, d: Decision) => {
      setDecisions((prev) => ({ ...prev, [`${rowIdx}:${col}`]: d }));
    }, []);

    // Parse rows
    const parsedRows = useMemo(
      () =>
        data.map((row) => {
          const changes = parseChanges(row.changes);
          return {
            diffType: String(row.diff_type ?? '').toLowerCase() as DiffType,
            oldValues: parseGoMap(row.old_values) ?? {},
            newValues: parseGoMap(row.new_values) ?? {},
            // changedFields: Set of column names for quick lookup
            changedFields: new Set(changes.map((c) => c.column)),
            // changesDetail: full change info with old/new values per field
            changesDetail: changes,
          };
        }),
      [data]
    );

    const handleAcceptAllNew = useCallback(() => {
      const next: Record<string, Decision> = {};
      parsedRows.forEach(({ diffType, changedFields }, idx) => {
        if (diffType === 'changed') {
          changedFields.forEach((col) => { next[`${idx}:${col}`] = 'new'; });
        }
      });
      setDecisions(next);
    }, [parsedRows]);

    const handleKeepAllOld = useCallback(() => setDecisions({}), []);

    // Ordered column list derived from the first row that has values
    const dataColumns = useMemo(() => {
      const seen = new Set<string>();
      const order: string[] = [];
      for (const row of parsedRows) {
        const src = Object.keys(row.oldValues).length > 0 ? row.oldValues : row.newValues;
        if (Object.keys(src).length === 0) continue;
        for (const k of Object.keys(src)) {
          if (!seen.has(k)) { seen.add(k); order.push(k); }
        }
        break;
      }
      for (const row of parsedRows) {
        for (const k of [...Object.keys(row.oldValues), ...Object.keys(row.newValues)]) {
          if (!seen.has(k)) { seen.add(k); order.push(k); }
        }
      }
      return order;
    }, [parsedRows]);

    // Expose export helper to parent (used by download button)
    useImperativeHandle(ref, () => ({
      getExportData() {
        const STATUS_LABEL: Record<string, string> = {
          added: '新增', removed: '删除', changed: '变更', unchanged: '未变',
        };
        const exportColumns = ['变更状态', ...dataColumns];
        const exportRows: string[][] = parsedRows.map(
          ({ diffType, oldValues, newValues, changesDetail }, idx) => {
            const changedSet = new Set(changesDetail.map((c) => c.column));
            const status = STATUS_LABEL[diffType] ?? diffType;
            const dataCells = dataColumns.map((col) => {
              if (diffType === 'added')   return newValues[col] ?? '';
              if (diffType === 'removed') return oldValues[col] ?? '';
              if (diffType === 'changed' && changedSet.has(col)) {
                const d = decisions[`${idx}:${col}`] ?? 'old';
                return d === 'new' ? (newValues[col] ?? '') : (oldValues[col] ?? '');
              }
              return oldValues[col] ?? newValues[col] ?? '';
            });
            return [status, ...dataCells];
          }
        );
        return { columns: exportColumns, rows: exportRows };
      },
    }), [parsedRows, dataColumns, decisions]);

    const counts = useMemo(() => {
      const c = { added: 0, removed: 0, changed: 0, unchanged: 0 };
      parsedRows.forEach((r) => {
        if      (r.diffType === 'added')   c.added++;
        else if (r.diffType === 'removed') c.removed++;
        else if (r.diffType === 'changed') c.changed++;
        else                               c.unchanged++;
      });
      return c;
    }, [parsedRows]);

    if (!columns.includes('diff_type')) {
      return (
        <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
          <Warning className="w-8 h-8 text-amber-400 mb-2" />
          <p className="text-sm text-zinc-500">数据格式不符合差异对比结果格式</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        <SummaryBanner
          total={data.length}
          {...counts}
          onAcceptAllNew={counts.changed > 0 ? handleAcceptAllNew : undefined}
          onKeepAllOld={counts.changed > 0 ? handleKeepAllOld : undefined}
        />

        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="w-9 px-2 py-2 text-right text-zinc-400 border-r border-zinc-100 font-normal select-none">
                  #
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 border-r border-zinc-100 whitespace-nowrap w-20">
                  状态
                </th>
                {dataColumns.map((col) => (
                  <th
                    key={col}
                    className="px-3 py-2 text-left font-semibold text-zinc-600 border-r border-zinc-100 last:border-r-0 whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsedRows.map(({ diffType, oldValues, newValues, changedFields, changesDetail }, idx) => {
                const style = DIFF_STYLE[diffType] ?? fallbackStyle;
                const Icon = style.icon;
                return (
                  <motion.tr
                    key={idx}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: Math.min(idx * 0.008, 0.25) }}
                    className={`${style.rowBg} border-b border-zinc-100`}
                  >
                    <td className="px-2 py-2 text-zinc-400 border-r border-zinc-100 text-right select-none tabular-nums align-middle">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2 border-r border-zinc-100 align-middle">
                      <div className="flex items-center gap-1">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${style.badge}`}
                        >
                          <Icon className={`w-2.5 h-2.5 ${style.iconCls}`} weight="bold" />
                          {style.label}
                        </span>
                        {diffType === 'changed' && changesDetail.length > 0 && (
                          <span className="text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 whitespace-nowrap">
                            {changesDetail.length}处
                          </span>
                        )}
                      </div>
                    </td>
                    {dataColumns.map((col) => (
                      <DiffCell
                        key={col}
                        diffType={diffType}
                        oldVal={oldValues[col]}
                        newVal={newValues[col]}
                        isChanged={changedFields.has(col)}
                        decision={decisions[`${idx}:${col}`] ?? 'old'}
                        onDecide={(d) => handleDecide(idx, col, d)}
                      />
                    ))}
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);
